import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { 
  Trash2, 
  RefreshCw, 
  Database, 
  HardDrive, 
  Smartphone, 
  Monitor,
  AlertTriangle,
  CheckCircle,
  Clock
} from "lucide-react";
import { getStorageQuota, performEmergencyCleanup, cleanExpiredLocalStorage, getDeviceInfo } from "@/utils/browserStorageCleanup";
import { sendCleanupCommandToAllUsers } from "@/utils/remoteCleanupService";

interface StorageStats {
  used: number;
  total: number;
  percentage: number;
  localStorageSize: number;
  cacheCount: number;
  deviceInfo: any;
}

export function StorageManagementSection() {
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCleanup, setLastCleanup] = useState<string>('');
  const { toast } = useToast();

  const loadStorageStats = async () => {
    setIsLoading(true);
    try {
      const quota = await getStorageQuota();
      const deviceInfo = getDeviceInfo();
      
      // Calculate localStorage size
      const localStorageSize = JSON.stringify(localStorage).length;
      
      // Count caches
      let cacheCount = 0;
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        cacheCount = cacheNames.length;
      }
      
      setStorageStats({
        used: quota.used,
        total: quota.total,
        percentage: quota.percentage,
        localStorageSize,
        cacheCount,
        deviceInfo
      });
      
      setLastCleanup(deviceInfo.lastCleanup);
    } catch (error) {
      console.error('Failed to load storage stats:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել պահեստի վիճակագրությունը",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const performCleanup = async (type: 'emergency' | 'expired') => {
    setIsLoading(true);
    try {
      let cleanedCount = 0;
      
      if (type === 'emergency') {
        cleanedCount = await performEmergencyCleanup();
        toast({
          title: "Հաջողություն",
          description: `Արտակարգ մաքրում կատարված: ${cleanedCount} տարր հեռացված`,
          variant: "default"
        });
      } else {
        cleanedCount = cleanExpiredLocalStorage();
        toast({
          title: "Հաջողություն", 
          description: `Ժամկետանց տվյալներ մաքրված: ${cleanedCount} տարր հեռացված`,
          variant: "default"
        });
      }
      
      // Reload stats
      await loadStorageStats();
    } catch (error) {
      console.error('Cleanup failed:', error);
      toast({
        title: "Սխալ",
        description: "Մաքրումը ձախողվեց",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllCache = async () => {
    if (!confirm('Վստա՞հ եք, որ ուզում եք մաքրել բոլոր cache-ները: Սա կարող է ազդել կայքի արագության վրա:')) {
      return;
    }
    
    setIsLoading(true);
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        
        toast({
          title: "Հաջողություն",
          description: `${cacheNames.length} cache մաքրված`,
          variant: "default"
        });
        
        await loadStorageStats();
      }
    } catch (error) {
      console.error('Cache clearing failed:', error);
      toast({
        title: "Սխալ",
        description: "Cache մաքրումը ձախողվեց",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendCleanupToAllUsers = async () => {
    if (!confirm('Վստա՞հ եք, որ ուզում եք ուղարկել մաքրման հրաման բոլոր օգտատերերին:')) {
      return;
    }

    setIsLoading(true);
    try {
      const { sendCleanupCommandToAllUsers } = await import('@/utils/remoteCleanupService');
      const success = await sendCleanupCommandToAllUsers({ 
        type: 'emergency_cleanup', 
        params: { force: true } 
      });
      
      if (success) {
        toast({
          title: "Հրաման ուղարկված",
          description: "Բոլոր օգտատերերին ուղարկվել է cache մաքրման հրաման",
        });
      } else {
        throw new Error('Failed to send cleanup command');
      }
    } catch (error) {
      console.error('Failed to send cleanup command:', error);
      toast({
        title: "Սխալ",
        description: "Հրամանի ուղարկումը ձախողվեց",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendGlobalCacheUpdate = async () => {
    if (!confirm('Վստա՞հ եք, որ ուզում եք թարմացնել բոլոր օգտատերերի cache-ները:')) {
      return;
    }

    setIsLoading(true);
    try {
      const { sendCleanupCommandToAllUsers } = await import('@/utils/remoteCleanupService');
      const success = await sendCleanupCommandToAllUsers({ 
        type: 'update_cache_version', 
        params: { newVersion: 'v5', force: true } 
      });
      
      if (success) {
        toast({
          title: "Cache թարմացում ուղարկված",
          description: "Բոլոր օգտատերերին ուղարկվել է cache թարմացման հրաման",
        });
      } else {
        throw new Error('Failed to send cache update command');
      }
    } catch (error) {
      console.error('Failed to send cache update command:', error);
      toast({
        title: "Սխալ",
        description: "Cache թարմացման հրամանի ուղարկումը ձախողվեց",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const autoUpdateCachesForAll = async () => {
    setIsLoading(true);
    try {
      const { sendCleanupCommandToAllUsers } = await import('@/utils/remoteCleanupService');
      
      // Send cache update command
      const cacheSuccess = await sendCleanupCommandToAllUsers({ 
        type: 'update_cache_version', 
        params: { newVersion: 'v5', force: true } 
      });
      
      // Send service worker update command  
      const swSuccess = await sendCleanupCommandToAllUsers({ 
        type: 'force_service_worker_update', 
        params: { force: true } 
      });
      
      if (cacheSuccess && swSuccess) {
        toast({
          title: "Ավտոմատ թարմացում հաջողված",
          description: "Բոլոր օգտատերերի cache-ները և service worker-ները ավտոմատ թարմացվել են",
        });
      } else {
        throw new Error('Failed to send auto update commands');
      }
    } catch (error) {
      console.error('Failed to auto update:', error);
      toast({
        title: "Սխալ",
        description: "Ավտոմատ թարմացումը ձախողվեց",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const forceGlobalServiceWorkerUpdate = async () => {
    if (!confirm('Վստա՞հ եք, որ ուզում եք ստիպված թարմացնել բոլոր օգտատերերի Service Worker-ները: Սա կվերաբեռնի բոլոր բաց էջերը:')) {
      return;
    }

    setIsLoading(true);
    try {
      const { sendCleanupCommandToAllUsers } = await import('@/utils/remoteCleanupService');
      const success = await sendCleanupCommandToAllUsers({ 
        type: 'force_service_worker_update', 
        params: { force: true } 
      });
      
      if (success) {
        toast({
          title: "SW թարմացում ուղարկված",
          description: "Բոլոր օգտատերերին ուղարկվել է Service Worker թարմացման հրաման",
        });
      } else {
        throw new Error('Failed to send SW update command');
      }
    } catch (error) {
      console.error('Failed to send SW update command:', error);
      toast({
        title: "Սխալ", 
        description: "SW թարմացման հրամանի ուղարկումը ձախողվեց",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStorageStats();
  }, []);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStorageStatus = (percentage: number) => {
    if (percentage >= 90) return { color: 'destructive', icon: AlertTriangle, text: 'Վտանգավոր' };
    if (percentage >= 70) return { color: 'default', icon: AlertTriangle, text: 'Ուշադրություն' };
    return { color: 'default', icon: CheckCircle, text: 'Բարելավ' };
  };

  if (!storageStats) {
    return (
      <Card className="border-border/70 bg-card/80">
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Պահեստի Կառավարում
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const status = getStorageStatus(storageStats.percentage);
  const StatusIcon = status.icon;

  return (
    <div className="space-y-6">
      {/* Storage Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Պահեստի Ամփոփ Տեղեկություններ
          </CardTitle>
          <CardDescription>
            Browserի պահեստի օգտագործման վիճակագրություն
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${status.color === 'destructive' ? 'text-destructive' : 'text-muted-foreground'}`} />
              <span className="font-medium">Վիճակ:</span>
            </div>
            <Badge variant={status.color as any}>{status.text}</Badge>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Օգտագործված:</span>
              <span>{formatBytes(storageStats.used)} / {formatBytes(storageStats.total)}</span>
            </div>
            <Progress value={storageStats.percentage} className="h-2" />
            <div className="text-center text-sm text-muted-foreground">
              {storageStats.percentage}% օգտագործում
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">LocalStorage:</span>
              <span className="text-sm font-medium">{formatBytes(storageStats.localStorageSize)}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Cache-ներ:</span>
              <span className="text-sm font-medium">{storageStats.cacheCount} հատ</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Device Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {storageStats.deviceInfo.userAgent.includes('Mobile') ? 
              <Smartphone className="h-5 w-5" /> : 
              <Monitor className="h-5 w-5" />
            }
            Սարքավորման Տեղեկություններ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Սարքավորման ID:</span>
              <p className="break-all text-muted-foreground">{storageStats.deviceInfo.id}</p>
            </div>
            
            <div>
              <span className="font-medium">Առաջին այցը:</span>
              <p className="text-muted-foreground">{new Date(storageStats.deviceInfo.firstVisit).toLocaleDateString('hy-AM')}</p>
            </div>
            
            <div>
              <span className="font-medium">Վերջին մաքրումը:</span>
              <p className="text-muted-foreground">{new Date(storageStats.deviceInfo.lastCleanup).toLocaleDateString('hy-AM')}</p>
            </div>
            
            <div>
              <span className="font-medium">Մաքրումների քանակ:</span>
              <p className="text-muted-foreground">{storageStats.deviceInfo.cleanupCount} հատ</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cleanup Actions */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Մաքրման Գործողություններ
          </CardTitle>
          <CardDescription>
            Ձեռքով մաքրման գործիքներ
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              onClick={() => performCleanup('expired')}
              disabled={isLoading}
              variant="outline"
              className="min-h-11 w-full justify-center gap-2 whitespace-normal"
            >
              <Clock className="h-4 w-4" />
              Ժամկետանց տվյալներ
            </Button>
            
            <Button
              onClick={() => performCleanup('emergency')}
              disabled={isLoading}
              variant="outline"
              className="min-h-11 w-full justify-center gap-2 whitespace-normal"
            >
              <AlertTriangle className="h-4 w-4" />
              Արտակարգ մաքրում
            </Button>
            
            <Button
              onClick={clearAllCache}
              disabled={isLoading}
              variant="outline"
              className="min-h-11 w-full justify-center gap-2 whitespace-normal"
            >
              <Database className="h-4 w-4" />
              Բոլոր Cache-ները
            </Button>
            
            <Button
              onClick={loadStorageStats}
              disabled={isLoading}
              variant="outline"
              className="min-h-11 w-full justify-center gap-2 whitespace-normal"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Թարմացնել
            </Button>
          </div>

          <Separator className="my-4" />

            <div className="space-y-4">
              <h4 className="font-medium">Ծանուցումներ բոլոր օգտատերերին</h4>
              
              {/* Auto Update Button */}
              <div className="p-4 bg-primary/5 rounded-lg border-2 border-primary/20">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <h5 className="font-medium text-primary">Ավտոմատ Թարմացում</h5>
                    <p className="text-sm text-muted-foreground">Մեկ սեղմումով թարմացրեք բոլոր օգտատերերի cache-ները</p>
                  </div>
                  <Button 
                    onClick={autoUpdateCachesForAll} 
                    className="min-h-11 w-full bg-primary font-medium text-primary-foreground hover:bg-primary/90 sm:w-auto"
                    disabled={isLoading}
                    size="lg"
                  >
                    <RefreshCw className={`h-5 w-5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    {isLoading ? 'Թարմացվում...' : 'Ավտոմատ Թարմացում'}
                  </Button>
                </div>
              </div>
              
              {/* Individual Update Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button
                  onClick={sendCleanupToAllUsers}
                  disabled={isLoading}
                  variant="destructive"
                  className="min-h-11 w-full justify-center gap-2 whitespace-normal"
                >
                  <RefreshCw className="h-4 w-4" />
                  Մաքրման Հրաման
                </Button>
                
                <Button 
                  onClick={sendGlobalCacheUpdate} 
                  className="min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={isLoading}
                >
                  {isLoading ? 'Ուղարկվում...' : 'Cache Թարմացում'}
                </Button>
                
                <Button 
                  onClick={forceGlobalServiceWorkerUpdate} 
                  className="min-h-11 w-full bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  disabled={isLoading}
                >
                  {isLoading ? 'Ուղարկվում...' : 'SW Թարմացում'}
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Ավտոմատ թարմացումը կուղարկի և՛ cache թարմացման, և՛ service worker թարմացման հրամաններ բոլոր օգտատերերին
              </p>
            </div>
        </CardContent>
      </Card>
    </div>
  );
}